import { createStore } from 'vuex';
import sileo from 'sileo';
import axios from 'source/lib/axios';
import eventBus from 'source/lib/event-bus';
import {
    addWebsocketTargets,
    getSubscribedWebsocketTargets,
    removeWebsocketTargets
} from './websocketSubscriptions';

import auth from './auth';
import board from './board';
import mainBoard from './board/main-board';
import bookings from './bookings';
import campaign from './campaign';
import employers from './employers';
import employment from './employment';
import contract from './contract';
import payroll from './payroll';
import departments from './departments';
import onboarding from './onboarding';
import workforce from './workforce';
import registration from './auth/registration';
import company from './company';
import issues from './issues';
import jobOffer from './job-offer';
import platformSettings from './platform-settings';
import template from './template';
import policies from './policies';
import teams from './teams';
import paymentDues from './payment-dues';
import personalBoard from './board/personal-board';
import interviewRounds from './interview-rounds';
import exitInterviews from './exit-interviews';
import requests from './requests';
import memo from './memo';
import overviews from './overviews';
// DEPRECATED: taskSummary is deprecated, but will remove later.
import taskSummary from './task-summary';
import bposeats from './bposeats';
import milestone from './milestone';
import personalMilestone from './milestone/personal-milestone';
import negotiation from './negotiation';
import query from './query';
import scalema from './scalema';
import accountabilities from './accountabilities';
import permissions from './permissions';
import scheduledTasks from './scheduled-tasks';
import genericForm from './generic-form';
import shareableForm from './shareable-form';
import tag from './tag';
import boardActions from './board-actions';
import posting from './posting';
import help from './help';
import timezone from './timezone';
import timezoneSchedule from './timezoneSchedule';
import hrOnboarding from './hr-onboarding';
import timelog from './timelog';
import releaseNotes from './release-notes';
import supervisorManager from './supervisor-manager';
import notifications from './notifications';
import profile from './profile';
import pcn from './pcn';
import marketing from './marketing';
import administrativeLeave from './administrative-leave';
import staff from './staff';
import recruitment from './recruitment';
import steps from './recruitment/steps';
import jobOfferRequestsSteps from './recruitment/job-offer-requests-steps';
import concerns from './concerns';
import ticketBoard from './board/ticket-board';
import allowances from './allowances';
import workforceBoard from './board/workforce-board';
import recruitmentNotes from './recruitment-notes';
import todo from './todo';
import staffConfiguration from './staff-configuration';
import businessSignUp from './business-signup';
import resumes from './resumes';
import hrPortal from './hr-portal';
import emailSubscription from './email-subscription';

import taskEstimation from './task-estimation';
import generateEva from './generate-eva';
import gallery from './gallery';
import recentAccess from './recent-access';

const Features = new sileo.Model('bposeats', 'features');
const ExchangeRateHistorical = new sileo.Model(
    'exchange', 'historical');
const PayrollRelatedConstant = new sileo.Model(
    'bposeats', 'payroll-related-constants');
const Locations = new sileo.Model('bposeats', 'facility-location');
const TwoLevelJobFocus = new sileo.Model('bposeats', 'two-level-job-focus');
const Country = new sileo.Model('bposeats', 'country');
const JobPositions = new sileo.Model('bposeats', 'position');
const JobFocuses = new sileo.Model('bposeats', 'job-focus');
const ClientContact = new sileo.Model('hqzen', 'client-concerns');
const AmazonSettingsConstant = new sileo.Model('hqzen', 'amazon-settings-constants');
const TimezoneRegion = new sileo.Model('bposeats', 'timezone-region');


function websocketOnMessageHandler(message) {
    let { event, data } = JSON.parse(message.data);
    eventBus.$emit(event, data);
}


const state = {
    jobPositions: [],

    jobFocuses: [],

    currencies: [],

    currency: localStorage.getItem('currency') || 'USD',

    features: {},

    locations: [],

    twoLevelJobFocuses: [],

    jobFocusesAndSpecialties: {},

    conversionRatesByDate: {},

    baseCurrencyCode: 'USD',
    countries: [],
    countryError: '',


    sites: {
        APPLYBPO_URL: process.env.APPLYBPO_URL,
        BPOSEATS_URL: process.env.BPOSEATS_URL,
        HQZEN_URL: process.env.HQZEN_URL,
        SCALEMA_URL: process.env.SCALEMA_URL,
        BPOTUBE_URL: process.env.BPOTUBE_URL,
        BUILD_HASH: process.env.BUILD_HASH,
        BRANCH_ENV: process.env.BRANCH_ENV,
        API_URL: process.env.API_URL,
    },
    /* eslint-disable */

    exchangeDates: [],

    payrollConstants: {},

    genericCardsSeen: JSON.parse(localStorage.getItem('generic-cards-seen')) || [],

    websocketConnection: null,
    websocketRetryCount: 0,
    websocketTokenIsFetching: false,
    websocketSubscriptions: {},

    amazonSettings: {},

    exportCSV: {},
    showMobileSidebar: false

};

const getters = {
    currency: state => state.currency,
    currencies: state => state.currencies,
    twoLevelJobFocuses: state => state.twoLevelJobFocuses,
    jobFocusesAndSpecialties: state => state.jobFocusesAndSpecialties,
    locations: state => state.locations,
    features: state => state.features,
    sites: state => state.sites,
    countries: state => state.countries,
    genericCardsSeen: state => state.genericCardsSeen,
    jobPositions: state => state.jobPositions,
    jobFocuses: state => state.jobFocuses,
    amazonSettings: state => state.amazonSettings,
    exportCSV: state => state.exportCSV,
};

const mutations = {
    setFeatures(state, features) {
        state.features = features;
    },

    setCurrency(state, currency) {
        state.currency = currency;
        localStorage.setItem('currency', currency);
    },

    setCurrencies(state, currencies) {
        state.currencies = currencies;
    },

    setTwoLevelJobFocuses(state, twoLevelJobFocuses) {
        state.twoLevelJobFocuses = twoLevelJobFocuses;
    },

    setPayrollRelatedConstants(state, constants) {
        state.payrollConstants = constants;
    },

    setConversionRatesByDate(state, conversionRate) {
        let { data, date } = conversionRate;
        state.conversionRatesByDate[date] = data;
    },

    pushExchangeDate(state, date) {
        state.exchangeDates.push(date);
    },

    setWebsocketConnection(state, websocket) {
        state.websocketConnection = websocket;
    },

    setWebsocketRetryCount(state, retryCount) {
        state.websocketRetryCount = retryCount;
    },

    setWebsocketSubscriptions(state, subscriptions) {
        state.websocketSubscriptions = subscriptions;
    },

    setLocations(state, locations) {
        state.locations = locations;
    },

    updateJobFocusesAndSpecialties(state, jobFocusesAndSpecialties) {
        state.jobFocusesAndSpecialties = {...jobFocusesAndSpecialties };
    },
    setCountries(state, countries) {
        state.countries = countries;
    },
    setCountryError(state, error) {
        state.countryError = error;
    },

    setGenericCardAsSeen(state, genericCardName) {
        let cardAlreadySeen = state.genericCardsSeen.indexOf(genericCardName) !== -1;
        if (cardAlreadySeen) return;
        let genericCardsSeen = [...state.genericCardsSeen, genericCardName];
        state.genericCardsSeen = genericCardsSeen
        localStorage.setItem('generic-cards-seen', JSON.stringify(genericCardsSeen));
    },

    setJobPositions(state, jobPositions) {
        state.jobPositions = jobPositions;
    },

    setJobFocuses(state, jobFocuses) {
        state.jobFocuses = jobFocuses;
    },

    setAmazonSettingsConstant(state, constant) {
        state.amazonSettings = constant;
    },

    setExportCSV(state, exportCSV) {
        state.exportCSV = exportCSV;
    },

    toggleMobileSidebar(state){
        state.showMobileSidebar = !state.showMobileSidebar;
    },

};

const actions = {
    fetchFeatures({ commit }) {
        return Features.objects.filter().then(function(data) {
            commit('setFeatures', data);
            return data;
        });
    },

    fetchCurrencies({ commit, dispatch }) {
        let today = new Date().toISOString().split('T')[0];
        dispatch('fetchExchangeRatesByDate', [today]).then(function(data) {
            commit('setCurrencies', Object.keys(data).sort());
        });
    },

    fetchExportCSV({commit}, payload){
        return milestone.actions.downloadMilestonesViewExportCSV(payload).then(function(data) {
            commit('setExportCSV', data);
            return data;
        });
    },

    fetchTwoLevelJobFocuses({ commit }) {
        return new Promise((resolve, reject) => {
            TwoLevelJobFocus.objects.filter().then((data) => {
                commit('setTwoLevelJobFocuses', data);
                resolve(data);
            });
        });
    },

    updateJobFocusesAndSpecialties(state, jobFocusesAndSpecialties) {
        state.jobFocusesAndSpecialties = {...jobFocusesAndSpecialties };
    },

    fetchPayrollConstants({ commit }) {
        return PayrollRelatedConstant.objects.filter().then((data) => {
            commit('setPayrollRelatedConstants', data);
            return data;
        });
    },

    fetchLocations({ commit }) {
        return new Promise((resolve) => {
            Locations.objects.filter().then(function(data) {
                commit('setLocations', data);
                resolve();
            });
        });
    },

    fetchJobPositions({ commit }) {
        JobPositions.objects.filter().then(function(data) {
            commit('setJobPositions', data);
        });
    },

    fetchJobFocuses({ commit }) {
        return JobFocuses.objects.filter().then(function(data) {
            commit('setJobFocuses', data);
        });
    },

    // Date must be in YYYY-MM-DD format
    fetchExchangeRatesByDate({ commit, state }, date) {
        if (date in state.conversionRatesByDate) {
            return state.conversionRatesByDate[date];
        }

        if (state.exchangeDates.indexOf(date) !== -1) {
            return;
        }

        commit('pushExchangeDate', date);
        return ExchangeRateHistorical.objects.filter({ date }).then((data) => {
            commit('setConversionRatesByDate', { date, data });
            return data;
        });
    },

    async websocketReconnect({ dispatch, state }) {
        const wait = ms => new Promise(resolve => setTimeout(resolve, ms));
        const k = Math.floor(Math.random() * state.websocketRetryCount);

        // Clamp retry count to a max of 12.
        if (state.websocketRetryCount < 12) {
            state.websocketRetryCount++;
        } else {
            return;
        }

        await wait(Math.pow(2, k));
        await dispatch('initiateWebsocketConnection');
    },

    closeWebsocketConnection({ state, commit }) {
        state.websocketConnection?.close();
        commit('setWebsocketConnection', null);
    },

    sendWebsocketTargets({ state }, { event, targets }) {
        if (!state.websocketConnection || state.websocketConnection.readyState !== WebSocket.OPEN) {
            return;
        }

        if (!targets.length) {
            return;
        }

        state.websocketConnection.send(JSON.stringify({ event, targets }));
    },

    replayWebsocketSubscriptions({ state, dispatch }) {
        const targets = getSubscribedWebsocketTargets(state.websocketSubscriptions);
        dispatch('sendWebsocketTargets', {
            event: 'subscribe',
            targets,
        });
    },

    async subscribeWebsocketTargets({ state, commit, dispatch }, targets) {
        const { entries, addedTargets } = addWebsocketTargets(
            state.websocketSubscriptions,
            targets,
        );

        commit('setWebsocketSubscriptions', entries);

        if (!addedTargets.length) {
            return;
        }

        if (!state.websocketConnection && !state.websocketTokenIsFetching) {
            await dispatch('initiateWebsocketConnection');
        }

        dispatch('sendWebsocketTargets', {
            event: 'subscribe',
            targets: addedTargets,
        });
    },

    unsubscribeWebsocketTargets({ state, commit, dispatch }, targets) {
        const { entries, removedTargets } = removeWebsocketTargets(
            state.websocketSubscriptions,
            targets,
        );

        commit('setWebsocketSubscriptions', entries);
        dispatch('sendWebsocketTargets', {
            event: 'unsubscribe',
            targets: removedTargets,
        });
    },

    async initiateWebsocketConnection({ dispatch, commit, state }) {
        if (state.websocketConnection || state.websocketTokenIsFetching) return;

        state.websocketTokenIsFetching = true;

        let fetchWebsocketAuthToken = auth.actions.getWebsocketToken;
        let token = undefined;

        try {
            token = await fetchWebsocketAuthToken(auth.state);
        } catch (error) {
            console.warn(error);
            state.websocketTokenIsFetching = false;
            await dispatch('websocketReconnect');
        }
        state.websocketTokenIsFetching = false;

        if (!token) {
            return;
        }

        let protocol = process.env.API_URL === 'http://localhost:8000' ? 'ws://' : 'wss://';
        let host = process.env.API_URL.split('//')[1];

        const websocket = new WebSocket(
            protocol + host + '/websocket/' + '?token=' + token
        );
        // replay subscriptions after reconnecting
        websocket.addEventListener('open', () => {
            commit('setWebsocketRetryCount', 0);
            dispatch('replayWebsocketSubscriptions');
        });

        websocket.addEventListener('close', async (_event) => {
            commit('setWebsocketConnection', null);
            if (auth.state.user) {
                await dispatch('websocketReconnect');
            }
        });

        websocket.addEventListener('message', websocketOnMessageHandler);

        commit('setWebsocketConnection', websocket);
    },
    // Start sending periodic requests
    initiatePolling({ state }, event) {
        if (state.websocketConnection) {
            const sendRequest = () => {
                const requestData = { event: event };
                state.websocketConnection.send(JSON.stringify(requestData));
            };

            // Send the request every 1 minute
            state.pollingInterval = setInterval(sendRequest, 60000);
        }
    },

    // Stop sending periodic requests
    terminatePolling({ state }) {
        clearInterval(state.pollingInterval);
        state.pollingInterval = null;
    },

    fetchCountries({ commit }) {
        return new Promise((resolve) => {
            Country.objects
                .filter()
                .then((data) => {
                    commit('setCountries', data);
                    resolve(data)
                })
                .catch((error) => {
                    commit('setCountryError', error);
                });
        });
    },

    sendClientContact(context, contact) {
        const formData = new FormData();
        formData.append('email', contact);
        return ClientContact.objects.create(formData);
    },

    fetchAmazonSettingsConstant({ commit }) {
        return AmazonSettingsConstant.objects.filter().then(data => {
            commit('setAmazonSettingsConstant', data);
            return data;
        });
    },

    fetchTimezoneRegions({ commit, state }) {
        return TimezoneRegion.objects.filter();
    },

    fetchUserProfileCount(_) {
        return axios.get('hqzen/user-profile-count/');
    },

    fetchJobPostingCount(_) {
        return axios.get('hqzen/job-posting-count/');
    },

    checkSetJobPreferences(_) {
        return axios.get('hqzen/has-job-preferences/');
    },

    fetchProfileCompletion(_) {
        return axios.get('hqzen/profile-completion-progress/');
    }
};

const generics = {
    namespaced: true,
    state,
    getters,
    mutations,
    actions
};

const store = createStore({
    actions: {
        subscribeWebsocketTargets({ dispatch }, targets) {
            return dispatch('generics/subscribeWebsocketTargets', targets);
        },

        unsubscribeWebsocketTargets({ dispatch }, targets) {
            return dispatch('generics/unsubscribeWebsocketTargets', targets);
        },
    },

    modules: {
        generics,
        auth,
        board,
        mainBoard,
        bookings,
        campaign,
        employers,
        employment,
        contract,
        departments,
        onboarding,
        payroll,
        workforce,
        registration,
        company,
        issues,
        jobOffer,
        platformSettings,
        template,
        policies,
        paymentDues,
        personalBoard,
        teams,
        interviewRounds,
        requests,
        memo,
        overviews,
        exitInterviews,
        taskSummary,
        bposeats,
        milestone,
        personalMilestone,
        negotiation,
        query,
        scalema,
        accountabilities,
        permissions,
        scheduledTasks,
        genericForm,
        shareableForm,
        tag,
        boardActions,
        posting,
        help,
        timezone,
        timezoneSchedule,
        hrOnboarding,
        releaseNotes,
        notifications,
        timelog,
        releaseNotes,
        supervisorManager,
        profile,
        pcn,
        marketing,
        administrativeLeave,
        staff,
        recruitment,
        concerns,
        ticketBoard,
        allowances,
        workforceBoard,
        recruitmentNotes,
        todo,
        resumes,
        staffConfiguration,
        businessSignUp,
        steps,
        taskEstimation,
        generateEva,
        hrPortal,
        jobOfferRequestsSteps,
        gallery,
        emailSubscription,
        recentAccess,
    }
});

export default store;
